import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr } from "../../lib/api";
import { Card, SectionTitle, btnPrimary, Alert } from "../ui-bits";
import { toast } from "sonner";
import { Save, Loader2, Upload, Music, Play, Pause, Trash2, ArrowUp, ArrowDown, Plus, Check, Captions, Sparkles } from "lucide-react";

const TRANSFORM_IDS = ["Espejo", "Color correction", "Crop 97%", "Film grain", "Retro"];
// Overlays ambientales en bucle (julio-2026) — ver hyperframes-service/templates/particulas-*
// y backend/server.py::PARTICLE_OVERLAYS. Selección única (no checkbox como TRANSFORM_IDS):
// no tiene sentido superponer varios a la vez.
const PARTICLE_FILTER_IDS = ["polvo", "niebla", "nieve", "grano", "humo", "vapor", "lluvia", "ceniza", "chispas", "burbujas", "rayos", "destellos", "bokeh"];
// Reactivado julio-2026 (ver ROADMAP.md "Preset de transiciones Documental") — ver
// backend/server.py::run_render, mismo cambio en el otro lado.
const TRANSITIONS_UI_ENABLED = true;

export default function TabComposicion({ project, config, refreshProject, setTab }) {
  const { t } = useTranslation();
  const c = project?.composition || {};
  const [transitions, setTransitions] = useState(c.transitions?.length ? c.transitions : (c.transition ? [c.transition] : ["Corte directo"]));
  const [subtitles, setSubtitles] = useState(c.subtitles !== undefined ? c.subtitles : true);
  const [highlights, setHighlights] = useState(c.highlights !== undefined ? c.highlights : true);
  const [volume, setVolume] = useState(c.music_volume ?? 25);
  const [transforms, setTransforms] = useState(c.transforms || []);
  const [particleFilter, setParticleFilter] = useState(c.particle_filter || "");
  const [supermega, setSupermega] = useState(c.supermega || false);
  const [musicTracks, setMusicTracks] = useState([]);
  const [selected, setSelected] = useState(c.music_tracks || []);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const fileRef = useRef(null);
  const audioRef = useRef(null);

  const loadMusic = async () => {
    try {
      const { data } = await api.get(`/projects/${project.id}/music`);
      setMusicTracks(data.tracks || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { loadMusic(); }, []); // eslint-disable-line

  // Biblioteca de música de la plataforma (CC BY, créditos automáticos en la descripción)
  const [libTracks, setLibTracks] = useState([]);
  const [libMoods, setLibMoods] = useState([]);
  const [libMood, setLibMood] = useState("");
  const [libSeeding, setLibSeeding] = useState(false);
  const [addingLibId, setAddingLibId] = useState(null);

  const loadLibrary = async (mood = libMood) => {
    try {
      const { data } = await api.get(`/music-library${mood ? `?mood=${mood}` : ""}`);
      setLibTracks(data.tracks || []);
      setLibMoods(data.moods || []);
      setLibSeeding(!!data.seeding);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { loadLibrary(""); }, []); // eslint-disable-line

  const addFromLibrary = async (tr) => {
    setAddingLibId(tr.id);
    try {
      const { data } = await api.post(`/projects/${project.id}/music/from-library`, { track_id: tr.id });
      await loadMusic();
      const next = [...selected, data.id];
      setSelected(next);
      persist(next);
      toast.success(t("comp.libAdded", { name: tr.title }));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setAddingLibId(null); }
  };

  const toggleTransition = (tr) =>
    setTransitions((prev) => (prev.includes(tr) ? prev.filter((x) => x !== tr) : [...prev, tr]));
  const toggleTransform = (id) =>
    setTransforms((tf) => (tf.includes(id) ? tf.filter((x) => x !== id) : [...tf, id]));

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("title", f.name);
      await api.post(`/projects/${project.id}/music/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("comp.musicUploaded", { name: f.name }));
      await loadMusic();
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeTrack = async (id) => {
    try {
      await api.delete(`/projects/${project.id}/music/${id}`);
      const next = selected.filter((x) => x !== id);
      setSelected(next);
      await loadMusic();
      persist(next);
      toast.success(t("comp.trackDeleted"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const playTrack = (tr) => {
    if (playingId === tr.id && audioRef.current) { audioRef.current.pause(); setPlayingId(null); return; }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(tr.url);
    audioRef.current = a;
    a.onended = () => setPlayingId(null);
    a.play().then(() => setPlayingId(tr.id)).catch(() => toast.error(t("comp.playError")));
  };

  // Guarda la selección/orden de música al instante para que no se pierda al cambiar de pestaña.
  const persist = async (nextSelected, nextVolume = volume) => {
    try {
      await api.put(`/projects/${project.id}/composition`, {
        transitions, subtitles, highlights,
        music_tracks: nextSelected, music_volume: nextVolume, transforms,
        particle_filter: particleFilter, supermega,
      });
      refreshProject();
    } catch (e) { /* guardado silencioso */ }
  };

  const addToOrder = (id) => {
    if (selected.includes(id)) return;
    const next = [...selected, id];
    setSelected(next);
    persist(next);
  };
  const removeFromOrder = (id) => {
    const next = selected.filter((x) => x !== id);
    setSelected(next);
    persist(next);
  };
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= selected.length) return;
    const next = [...selected];
    [next[idx], next[j]] = [next[j], next[idx]];
    setSelected(next);
    persist(next);
  };

  const trackById = (id) => musicTracks.find((tr) => tr.id === id);

  const save = async () => {
    if (transitions.length === 0) { toast.warning(t("comp.chooseTransition")); return; }
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}/composition`, {
        transitions, subtitles, highlights, music_tracks: selected, music_volume: volume, transforms,
        particle_filter: particleFilter, supermega,
      });
      await refreshProject();
      toast.success(t("comp.saved"));
      setTab("renderizar");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8">
      <SectionTitle sub={t("comp.subtitle")}>{t("comp.title")}</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* IZQUIERDA: transiciones, resolución, subtítulos */}
        <Card className="p-6 space-y-6">
          {TRANSITIONS_UI_ENABLED && (
            <div>
              <h3 className="text-xl font-bold font-head text-slate-900 mb-1">{t("comp.transitions")}</h3>
              <p className="text-sm text-slate-500 mb-3">{t("comp.transitionsHint")}</p>
              <div className="grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto vb-scroll" data-testid="transition-list">
                {(config?.transitions || []).map((tr) => {
                  const on = transitions.includes(tr);
                  return (
                    <button key={tr} data-testid={`transition-${tr}`} onClick={() => toggleTransition(tr)} className={`flex items-center gap-2 text-left border-2 rounded-lg px-3 py-3 text-base font-medium transition-colors ${on ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>
                      <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${on ? "bg-blue-600 text-white" : "border-2 border-slate-300"}`}>{on && <Check size={14} />}</span>
                      {t(`transitions.${tr}`, { defaultValue: tr })}
                    </button>
                  );
                })}
              </div>
              {transitions.length > 0 && <p className="text-sm text-slate-600 mt-2">{t("comp.chosen")} <b>{transitions.map((tr) => t(`transitions.${tr}`, { defaultValue: tr })).join(" → ")}</b></p>}
            </div>
          )}

          <label className="flex items-center gap-3 border-2 rounded-lg px-4 py-4 cursor-pointer transition-colors border-slate-200 hover:border-slate-300" data-testid="subtitles-toggle-label">
            <input type="checkbox" data-testid="subtitles-toggle" checked={subtitles} onChange={(e) => setSubtitles(e.target.checked)} className="w-6 h-6 accent-blue-600" />
            <Captions size={22} className="text-blue-600" />
            <div>
              <div className="text-base font-semibold text-slate-800">{t("comp.subtitlesTitle")}</div>
              <div className="text-sm text-slate-500">{t("comp.subtitlesDesc")}</div>
            </div>
          </label>

          <label className="flex items-center gap-3 border-2 rounded-lg px-4 py-4 cursor-pointer transition-colors border-slate-200 hover:border-slate-300" data-testid="highlights-toggle-label">
            <input type="checkbox" data-testid="highlights-toggle" checked={highlights} onChange={(e) => setHighlights(e.target.checked)} className="w-6 h-6 accent-blue-600" />
            <Sparkles size={22} className="text-blue-600" />
            <div>
              <div className="text-base font-semibold text-slate-800">{t("comp.highlightsTitle")}</div>
              <div className="text-sm text-slate-500">{t("comp.highlightsDesc")}</div>
            </div>
          </label>
        </Card>

        {/* DERECHA: música */}
        <Card className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold font-head text-slate-900 flex items-center gap-2"><Music size={22} className="text-blue-600" /> {t("comp.music")}</h3>
            <input ref={fileRef} type="file" accept="audio/*" onChange={upload} className="hidden" data-testid="music-file-input" />
            <button data-testid="upload-music-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center justify-center gap-2 min-h-[48px] bg-blue-600 text-white rounded-xl px-4 sm:px-5 text-base font-bold hover:bg-blue-700 disabled:opacity-50">
              {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />} {t("comp.uploadMusic")}
            </button>
          </div>

          {/* Biblioteca */}
          <div>
            <p className="text-sm font-semibold text-slate-700 mb-2">{t("comp.yourLibrary")}</p>
            {musicTracks.length === 0 ? (
              <Alert type="info">{t("comp.noMusic")}</Alert>
            ) : (
              <div className="space-y-2 max-h-[180px] overflow-y-auto vb-scroll" data-testid="music-library">
                {musicTracks.map((tr) => (
                  <div key={tr.id} className="flex items-center gap-2 border-2 border-slate-200 rounded-lg px-3 py-2">
                    <button data-testid={`play-music-${tr.id}`} onClick={() => playTrack(tr)} className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50 shrink-0">{playingId === tr.id ? <Pause size={18} /> : <Play size={18} />}</button>
                    <span className="text-base text-slate-800 truncate flex-1">{tr.title}</span>
                    <span className="text-xs text-slate-400 shrink-0">{tr.duration}s</span>
                    {selected.includes(tr.id)
                      ? <span className="text-xs font-bold text-emerald-600 shrink-0">{t("comp.added")}</span>
                      : <button data-testid={`add-music-${tr.id}`} onClick={() => addToOrder(tr.id)} className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-bold shrink-0"><Plus size={16} /> {t("comp.add")}</button>}
                    <button data-testid={`delete-music-${tr.id}`} onClick={() => removeTrack(tr.id)} className="text-slate-400 hover:text-red-600 p-1 shrink-0"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Biblioteca YTMethod: pistas curadas de la plataforma, filtrables por mood.
              Añadir crea una REFERENCIA en music_files (no duplica el fichero) y los créditos
              CC BY se añaden solos a la descripción del vídeo. */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-700">{t("comp.platformLibrary")}</p>
              {libSeeding && (
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Loader2 size={12} className="animate-spin" /> {t("comp.libSeeding")}
                </span>
              )}
            </div>
            <div className="flex gap-1.5 flex-wrap mb-2" data-testid="music-mood-filter">
              {["", ...libMoods].map((m) => (
                <button key={m || "all"} onClick={() => { setLibMood(m); loadLibrary(m); }}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                    libMood === m ? "bg-amber-500 border-amber-500 text-white"
                    : "border-slate-200 text-slate-600 hover:border-amber-400"}`}>
                  {m ? t(`comp.mood.${m}`) : t("comp.libAllMoods")}
                </button>
              ))}
            </div>
            {libTracks.length === 0 ? (
              <Alert type="info">{t("comp.libEmpty")}</Alert>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto vb-scroll" data-testid="platform-music-library">
                {libTracks.map((tr) => (
                  <div key={tr.id} className="flex items-center gap-2 border-2 border-amber-200 bg-amber-50/40 rounded-lg px-3 py-2">
                    <button data-testid={`play-lib-${tr.id}`} onClick={() => playTrack(tr)} className="text-amber-600 hover:text-amber-800 p-1 rounded-full hover:bg-amber-50 shrink-0">
                      {playingId === tr.id ? <Pause size={18} /> : <Play size={18} />}
                    </button>
                    <span className="text-base text-slate-800 truncate flex-1">{tr.title}</span>
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0">{t(`comp.mood.${tr.mood}`)}</span>
                    <span className="text-xs text-slate-400 shrink-0">{tr.duration}s</span>
                    <button data-testid={`add-lib-${tr.id}`} onClick={() => addFromLibrary(tr)} disabled={addingLibId === tr.id}
                      className="flex items-center gap-1 text-amber-700 hover:text-amber-900 text-sm font-bold shrink-0 disabled:opacity-50">
                      {addingLibId === tr.id ? <Loader2 size={14} className="animate-spin" /> : <Plus size={16} />} {t("comp.add")}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-400 mt-1.5">{t("comp.libCredits")}</p>
          </div>

          {/* Orden de reproducción */}
          {selected.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">{t("comp.playOrder")}</p>
              <div className="space-y-2" data-testid="music-order">
                {selected.map((id, idx) => {
                  const tr = trackById(id);
                  if (!tr) return null;
                  return (
                    <div key={id} className="flex items-center gap-2 bg-blue-50 border-2 border-blue-200 rounded-lg px-3 py-2">
                      <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                      <span className="text-base text-slate-800 truncate flex-1">{tr.title}</span>
                      <button data-testid={`music-up-${id}`} disabled={idx === 0} onClick={() => move(idx, -1)} className="p-1 text-slate-600 hover:text-blue-700 disabled:opacity-30"><ArrowUp size={18} /></button>
                      <button data-testid={`music-down-${id}`} disabled={idx === selected.length - 1} onClick={() => move(idx, 1)} className="p-1 text-slate-600 hover:text-blue-700 disabled:opacity-30"><ArrowDown size={18} /></button>
                      <button data-testid={`music-remove-${id}`} onClick={() => removeFromOrder(id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-base font-semibold text-slate-800 mb-2">{t("comp.musicVolume")} <span className="text-blue-600">{volume}%</span></label>
            <input data-testid="volume-slider" type="range" min="0" max="100" value={volume} onChange={(e) => setVolume(parseInt(e.target.value))} className="w-full h-3 accent-blue-600 cursor-pointer" />
          </div>
        </Card>
      </div>

      {/* Transformaciones (opcional) */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">{t("comp.transformsTitle")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="transforms-list">
          {TRANSFORM_IDS.map((id) => (
            <label key={id} className={`flex items-center gap-3 border-2 rounded-lg px-4 py-3 cursor-pointer transition-colors ${transforms.includes(id) ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
              <input type="checkbox" data-testid={`transform-${id}`} checked={transforms.includes(id)} onChange={() => toggleTransform(id)} className="w-5 h-5 accent-blue-600" />
              <span className="text-base font-medium text-slate-800">{t(`comp.transform.${id}.name`)}</span>
              <span className="text-sm text-slate-500 ml-auto">{t(`comp.transform.${id}.desc`)}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* Filtro de partículas (opcional, selección única) */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">{t("comp.particleFilterTitle")}</h3>
        <p className="text-sm text-slate-500 mb-3">{t("comp.particleFilterHint")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="particle-filter-list">
          <label className={`flex items-center gap-3 border-2 rounded-lg px-4 py-3 cursor-pointer transition-colors ${particleFilter === "" ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
            <input type="radio" name="particle-filter" data-testid="particle-filter-none" checked={particleFilter === ""} onChange={() => setParticleFilter("")} className="w-5 h-5 accent-blue-600" />
            <span className="text-base font-medium text-slate-800">{t("comp.particleFilter.none")}</span>
          </label>
          {PARTICLE_FILTER_IDS.map((id) => (
            <label key={id} className={`flex items-center gap-3 border-2 rounded-lg px-4 py-3 cursor-pointer transition-colors ${particleFilter === id ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
              <input type="radio" name="particle-filter" data-testid={`particle-filter-${id}`} checked={particleFilter === id} onChange={() => setParticleFilter(id)} className="w-5 h-5 accent-blue-600" />
              <span className="text-base font-medium text-slate-800">{t(`comp.particleFilter.${id}`)}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* MODO SUPERMEGA — prueba explícita: el montador exprime el catálogo entero (transiciones,
          partículas, rótulos) al máximo en vez de con criterio comedido. Mismo ajuste que el
          checkbox del modal de 1-clic, pero disponible también en la creación manual. */}
      <Card className="p-6">
        <label className="flex items-start gap-3 border-2 border-orange-200 bg-orange-50/60 rounded-lg px-4 py-3 cursor-pointer">
          <input type="checkbox" data-testid="supermega-toggle" checked={supermega} onChange={(e) => setSupermega(e.target.checked)}
            className="w-6 h-6 accent-orange-600 shrink-0 mt-0.5" />
          <span>
            <span className="block text-base font-semibold text-slate-800">{t("comp.supermegaTitle")}</span>
            <span className="block text-sm text-slate-600 mt-0.5">{t("comp.supermegaDesc")}</span>
          </span>
        </label>
      </Card>

      <button data-testid="save-composition-button" onClick={save} disabled={busy} className={`w-full ${btnPrimary}`}>
        {busy ? <Loader2 className="animate-spin" size={22} /> : <Save size={22} />} {t("comp.saveBtn")}
      </button>
    </div>
  );
}

